import { isGrpcRuntimeAvailable } from '../api/grpc/grpcRuntime';
import { pipeexRuntimeConfig } from '../config/pipeexRuntimeConfig';
import { clearAuthSessionStorage } from '../lib/authSession';
import { initializeFingerprint } from './authStoreFingerprint';
import { createLoginActions } from './authStoreLogin';
import { decodeJwt, parseStoredUser, releaseRuntimeSession } from './authStoreSession';
import type { AuthActions, AuthGet, AuthSet, AuthState, AuthStore, AuthUser } from './authStoreTypes';

const initialAuthState: AuthState = {
  accessToken: null,
  refreshToken: null,
  user: null,
  machineFingerprint: null,
  fingerprintLoading: false,
  fingerprintError: null,
  loading: false,
  error: null,
  hydrated: false,
};

function syncFromStorage(set: AuthSet): void {
  const accessToken = localStorage.getItem('accessToken');
  const refreshToken = localStorage.getItem('refreshToken');
  const parsedStoredUser = parseStoredUser(localStorage.getItem('user'));
  const decodedUser = accessToken ? decodeJwt(accessToken) : null;
  const hasMalformedPersistedToken = !!accessToken && !decodedUser;
  const hasDetachedStoredUser = !!parsedStoredUser && !accessToken;

  if (hasMalformedPersistedToken || hasDetachedStoredUser) {
    console.warn('[AuthStore] Clearing invalid persisted auth state', {
      hasMalformedPersistedToken,
      hasDetachedStoredUser,
    });
    clearAuthSessionStorage();
    set({ accessToken: null, refreshToken: null, user: null, hydrated: true });
    return;
  }

  set({ accessToken, refreshToken, user: parsedStoredUser ?? decodedUser, hydrated: true });
}

function hydrate(set: AuthSet, get: AuthGet): void {
  if (get().hydrated) return;
  get().syncFromStorage();

  if (get().user || isGrpcRuntimeAvailable() || !pipeexRuntimeConfig.useMockData || !pipeexRuntimeConfig.skipAuthInMockMode) return;
  const mockUser: AuthUser = { id: 'mock-user', username: 'test', role: 'Mock' };
  localStorage.setItem('accessToken', 'mock-access-token');
  localStorage.setItem('refreshToken', 'mock-refresh-token');
  localStorage.setItem('user', JSON.stringify(mockUser));
  set({
    accessToken: 'mock-access-token',
    refreshToken: 'mock-refresh-token',
    user: mockUser,
    hydrated: true,
  });
}

function createStateActions(set: AuthSet, get: AuthGet): Pick<AuthActions, 'hydrate' | 'syncFromStorage' | 'initializeFingerprint' | 'releaseRuntimeSession' | 'logout' | 'clearError'> {
  return {
    syncFromStorage: () => syncFromStorage(set),
    hydrate: () => hydrate(set, get),
    initializeFingerprint: (options = {}) => initializeFingerprint(set, options),
    releaseRuntimeSession: async (options = {}) => releaseRuntimeSession(get, options.preferKeepalive),
    logout: async () => {
      await get().releaseRuntimeSession();
      clearAuthSessionStorage();
      set({ accessToken: null, refreshToken: null, user: null, error: null });
    },
    clearError: () => set({ error: null }),
  };
}

export function createAuthStoreState(set: AuthSet, get: AuthGet): AuthStore {
  return {
    ...initialAuthState,
    ...createStateActions(set, get),
    ...createLoginActions(set, get),
  };
}
